import React, { useState, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { normalizeRole } from './utils/roles';
import { RecipeProvider } from './contexts/RecipeContext';
import ProtectedRoute from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import { LEGAL_URLS } from './lib/supabase';
import Login from './components/Login';
import Register from './components/Register';
import Sidebar from './components/Sidebar';
import MobileTopNav from './components/MobileTopNav';
import useMediaQuery from './hooks/useMediaQuery';
import './App.css';
import './styles/form.css';
import './styles/NuevaRecetaForm.css';
import './styles/recipeManager.css';
import './styles/recipeSheet.css';
import './styles/mealPlanSheet.css';
import './styles/tmc/tmc-ds.css';
import './styles/dashboard.css';
import './styles/mobileNav.css';
import './styles/portal.css';
import './styles/onboarding.css';

const Dashboard = lazy(() => import('./components/Dashboard'));
const AdminHome = lazy(() => import('./components/AdminHome'));
const RecipeList = lazy(() => import('./components/recipeList'));
const RecipeManager = lazy(() => import('./components/RecipeManager'));
const NuevaRecetaForm = lazy(() => import('./components/NuevaRecetaForm'));
const RecipeEdit = lazy(() => import('./components/RecipeEdit'));
const RecipeCardTest = lazy(() => import('./components/RecipeCardTest'));
const StreamlinedMealPlanForm = lazy(() => import('./components/StreamlinedMealPlanForm'));
const MenuWebsite = lazy(() => import('./components/menu-website'));
const Cuenta = lazy(() => import('./components/Cuenta'));
const MealPlanList = lazy(() => import('./components/MealPlanList'));
const MealPlanDetail = lazy(() => import('./components/MealPlanDetail'));
const UserList = lazy(() => import('./components/UserList'));
const ClientManagement = lazy(() => import('./components/ClientManagement'));
const ClientForm = lazy(() => import('./components/ClientForm'));
const IngredientTest = lazy(() => import('./components/IngredientTest'));
const RecipeCreationTest = lazy(() => import('./components/RecipeCreationTest'));
const ErrorHandlingTest = lazy(() => import('./components/ErrorHandlingTest'));
const PortalShell = lazy(() => import('./components/portal/PortalShell'));
const PortalLogin = lazy(() => import('./components/portal/PortalLogin'));
const PortalHome = lazy(() => import('./components/portal/PortalHome'));
const PortalPreferences = lazy(() => import('./components/portal/PortalPreferences'));
const PortalPlans = lazy(() => import('./components/portal/PortalPlans'));
const PortalPlanDetail = lazy(() => import('./components/portal/PortalPlanDetail'));
const PortalRecipes = lazy(() => import('./components/portal/PortalRecipes'));
const PortalRoute = lazy(() => import('./components/portal/PortalRoute'));
const ClientOnboardingWizard = lazy(() => import('./components/ClientOnboardingWizard'));
const PublicSharePage = lazy(() => import('./components/PublicSharePage'));

const isDev = process.env.NODE_ENV !== 'production';

function LegalRedirect({ url }) {
  React.useEffect(() => {
    window.location.replace(url);
  }, [url]);
  return <RouteLoading />;
}

function RouteLoading() {
  return (
    <div className="loading-container">
      <div className="loading-spinner">
        <div className="spinner" />
      </div>
      <p>Cargando…</p>
    </div>
  );
}

function AppShell({ children }) {
  const isDesktop = useMediaQuery('(min-width: 769px)');
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return true;
    return window.matchMedia('(min-width: 769px)').matches;
  });

  return (
    <div className={`app-container${!isSidebarOpen ? ' sidebar-collapsed' : ''}`}>
      {isDesktop ? (
        <Sidebar
          isOpen={isSidebarOpen}
          toggleSidebar={() => setIsSidebarOpen((open) => !open)}
        />
      ) : (
        <MobileTopNav />
      )}
      <Suspense fallback={<RouteLoading />}>{children}</Suspense>
    </div>
  );
}

// Public route component that redirects authenticated users
const PublicRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <RouteLoading />;
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
};

function HomePage() {
  const { profile } = useAuth();
  const role = normalizeRole(profile?.role);
  if (role === 'admin') {
    return <AdminHome />;
  }
  return <Dashboard />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        }
      />

      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AppShell>
              <HomePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/recipes"
        element={
          <ProtectedRoute>
            <AppShell>
              <RecipeList />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/recetas"
        element={
          <ProtectedRoute>
            <AppShell>
              <RecipeManager />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/nueva-receta"
        element={
          <ProtectedRoute>
            <AppShell>
              <NuevaRecetaForm />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/editar-receta/:recipeId"
        element={
          <ProtectedRoute>
            <AppShell>
              <RecipeEdit />
            </AppShell>
          </ProtectedRoute>
        }
      />

      <Route path="/terms" element={<LegalRedirect url={LEGAL_URLS.terms} />} />
      <Route path="/privacy" element={<LegalRedirect url={LEGAL_URLS.privacy} />} />

      {isDev ? (
        <>
      <Route
        path="/test-recipe-card"
        element={
          <ProtectedRoute>
            <AppShell>
              <RecipeCardTest />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/test-ingredients"
        element={
          <ProtectedRoute>
            <AppShell>
              <IngredientTest />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/test-recipe-creation"
        element={
          <ProtectedRoute>
            <AppShell>
              <RecipeCreationTest />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/test-error-handling"
        element={
          <ProtectedRoute>
            <AppShell>
              <ErrorHandlingTest />
            </AppShell>
          </ProtectedRoute>
        }
      />
        </>
      ) : null}

      <Route
        path="/meal-plans"
        element={
          <ProtectedRoute>
            <AppShell>
              <MealPlanList />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/meal-plans/:planId"
        element={
          <ProtectedRoute>
            <AppShell>
              <MealPlanDetail />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/create-meal-plan"
        element={
          <ProtectedRoute>
            <AppShell>
              <StreamlinedMealPlanForm />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute requiredRole="admin">
            <AppShell>
              <UserList />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/clients/new"
        element={
          <ProtectedRoute requiredRole="admin">
            <AppShell>
              <ClientForm />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/clients/:profileId/edit"
        element={
          <ProtectedRoute requiredRole="admin">
            <AppShell>
              <ClientForm />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/clients/:profileId"
        element={
          <ProtectedRoute requiredRole="admin">
            <AppShell>
              <ClientManagement />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/clients"
        element={
          <ProtectedRoute requiredRole="admin">
            <AppShell>
              <ClientManagement />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/menu"
        element={
          <ProtectedRoute>
            <AppShell>
              <MenuWebsite />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <AppShell>
              <Cuenta />
            </AppShell>
          </ProtectedRoute>
        }
      />

      <Route
        path="/invite/:token"
        element={
          <Suspense fallback={<RouteLoading />}>
            <ClientOnboardingWizard />
          </Suspense>
        }
      />
      <Route
        path="/share/:token"
        element={
          <Suspense fallback={<RouteLoading />}>
            <PublicSharePage />
          </Suspense>
        }
      />

      <Route
        path="/portal/login"
        element={
          <Suspense fallback={<RouteLoading />}>
            <PortalLogin />
          </Suspense>
        }
      />
      <Route
        path="/portal"
        element={
          <Suspense fallback={<RouteLoading />}>
            <PortalRoute>
              <PortalShell />
            </PortalRoute>
          </Suspense>
        }
      >
        <Route
          index
          element={
            <Suspense fallback={<RouteLoading />}>
              <PortalHome />
            </Suspense>
          }
        />
        <Route
          path="preferences"
          element={
            <Suspense fallback={<RouteLoading />}>
              <PortalPreferences />
            </Suspense>
          }
        />
        <Route
          path="plans"
          element={
            <Suspense fallback={<RouteLoading />}>
              <PortalPlans />
            </Suspense>
          }
        />
        <Route
          path="plans/:planId"
          element={
            <Suspense fallback={<RouteLoading />}>
              <PortalPlanDetail />
            </Suspense>
          }
        />
        <Route
          path="recipes"
          element={
            <Suspense fallback={<RouteLoading />}>
              <PortalRecipes />
            </Suspense>
          }
        />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <RecipeProvider>
          <Router>
            <AppRoutes />
          </Router>
        </RecipeProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
